/**
 * Game Router Composable
 * 統一處理遊戲路由跳轉邏輯
 *
 * 架構：
 * - 單例模式：使用 nuxtApp._gameRouterContext 避免閉包累積
 */

import { useMainStore } from '@/stores/main'

export const useGameRouter = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保閉包只創建一次，避免 SSR 環境下記憶體洩漏
  if (!nuxtApp._gameRouterContext) {
    const localePath = useLocalePath()
    const mainStore = useMainStore()
    const auth = useAuth()
    const { isGuest } = useAuthToken()
    const { $bridge } = nuxtApp

    /**
     * 載入 LuckySport 腳本並跳轉
     */
    const loadLuckSportScript = async (basePath = '/main') => {
      return new Promise((resolve, reject) => {
        const script = document?.createElement('script')
        if (script) {
          script.src = 'https://sprodm.uni247.xyz/dist/LuckySports.es.js'
          script.async = true
          script.type = 'module'
          script.onload = async () => {
            await navigateTo(localePath(`${basePath}/luckysport`))
            resolve()
          }
          script.onerror = (error) => {
            console.error('Failed to load LuckySports script:', error)
            reject(error)
          }
          document.head.appendChild(script)
        } else {
          reject(new Error('Cannot create script element'))
        }
      })
    }

    /**
     * 處理遊戲進入後的路由跳轉
     * @param {Object} gameData - 遊戲資料
     */
    const handleGameRouting = async (gameData) => {
      const { providerId } = gameData.game || gameData

      // 設定 playing game 狀態
      mainStore.setPlayingGame(gameData.game || gameData)
      auth.setPlay(true)

      // Portrait 鎖定處理
      if ($bridge?.available) {
        const portrait = (gameData.game || gameData).portrait
        $bridge.callHandler('lockPortrait', { portrait }, () => {
          console.log('[Bridge] Portrait Locked')
        })
      }

      // 根據提供商和訪客狀態進行路由跳轉
      const basePath = isGuest.value ? '/guest' : '/main'

      if (providerId === 'LUCKYSPORT') {
        await loadLuckSportScript(basePath)
      } else if (providerId === 'BETBY') {
        await navigateTo(localePath(`${basePath}/sportsbook`))
      } else {
        await navigateTo(localePath(`${basePath}/game`))
      }
    }

    /**
     * 處理遊戲退出路由
     */
    const handleGameExit = async () => {
      auth.setPlay(false)

      if ($bridge?.available) {
        $bridge.callHandler('lockPortrait', { portrait: 0 }, () => {
          console.log('[Bridge] Portrait Unlocked')
        })
      }

      // 根據訪客狀態回到對應的主頁
      const basePath = isGuest.value ? '/guest' : '/main'
      try {
        await navigateTo(localePath(basePath))
      } catch (error) {
        console.error(`[useGameRouter] Failed to navigate to ${basePath}:`, error)
      }
    }

    /**
     * 統一遊戲退出函數
     * 處理遊戲退出和路由跳轉
     */
    const exitCurrentGame = async () => {
      const game = useGame()

      try {
        await game.exitGame()
        await handleGameExit()
      } catch (error) {
        // 即使出錯也要確保能回到主頁
        await handleGameExit()
      }
    }

    // 創建單一的成功回調函數實例，避免每次調用都創建新閉包
    const successCallback = async (gameData) => {
      try {
        await handleGameRouting(gameData)
      } catch (error) {
        console.error('[useGameRouter] Route navigation failed:', error)
        throw error
      } finally {
        // 無論成功或失敗都要清除 loading 狀態
        mainStore.setLoadingGame(false)
      }
    }

    nuxtApp._gameRouterContext = {
      handleGameRouting,
      handleGameExit,
      exitCurrentGame,
      loadLuckSportScript,
      successCallback,
      cleanup() {},
    }
  }

  const ctx = nuxtApp._gameRouterContext

  /**
   * 創建統一的成功回調函數
   * 返回同一個函數引用，避免閉包累積
   */
  const createGameSuccessCallback = () => ctx.successCallback

  return {
    handleGameRouting: ctx.handleGameRouting,
    handleGameExit: ctx.handleGameExit,
    exitCurrentGame: ctx.exitCurrentGame,
    createGameSuccessCallback,
    loadLuckSportScript: ctx.loadLuckSportScript,
  }
}
